/* eslint-disable @typescript-eslint/naming-convention */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { disabled, form, minLength, required } from '@angular/forms/signals';

import { Store } from '@ngrx/store';
import { take, takeUntil } from 'rxjs/operators';

import { UserState } from '@ups/user';
import { AppRouterState, BaseComponent, environment } from '@ups/xplat/core';
import { getAppVersionInfo } from '@ups/xplat/web/utils';
import { WebSmartlookService } from '@ups/xplat/web/core';
import {
  HelpManagementService,
  HelpState,
  buildHelpdeskClientContext,
  helpdeskAppName,
} from '@ups/xplat/base/in-app-help';

import {
  buildHaloTicketPayload,
  webHelpdeskDevice,
} from './halo-itsm-ticket-payload.util';

@Component({
  selector: 'ups-halo-itsm-modal',
  templateUrl: './halo-itsm-modal.component.html',
  styleUrls: [],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HaloItsmModalComponent extends BaseComponent {
  smartlookService = inject(WebSmartlookService);
  version = getAppVersionInfo().versionName;

  categories: { id: number; value: string }[] | null = null;
  showModal = false;

  isSaving: boolean = false;

  RelatedInfo = '';

  document: HALOHelpDesk = {} as HALOHelpDesk;

  /**
   * WI 35852: the fields the user edits, as a signal form. Native selects seed
   * '' so their placeholder option shows (conventions.md, Signal forms).
   * Name and email are display-only; the old disabled + required pair never
   * validated (a disabled control is excluded from validity), so they are
   * disabled here and carry no rule.
   */
  readonly ticket = signal<HaloTicketFields>(
    emptyTicketFields(environment.haloITSMCategory!)
  );
  readonly f = form(this.ticket, (p) => {
    disabled(p.fullName);
    disabled(p.email);
    required(p.ticketTypeId);
    required(p.summary);
    required(p.detailsHTML);
    minLength(p.detailsHTML, 3);
    required(p.impact);
    required(p.urgency);
    required(p.category);
  });
  readonly isTicketValid = computed(() => this.f().valid());
  helpManagementService = inject(HelpManagementService);
  private cdr = inject(ChangeDetectorRef);

  constructor(private store: Store) {
    super();

    const parsedCategories: { id: number; value: string }[] = JSON.parse(
      environment.haloITSMCategories!
    );
    this.categories = parsedCategories;
    store
      .select(HelpState.selectHelpDesk)
      .pipe(takeUntil(this.destroy$))
      .subscribe((x) => {
        this.showModal = x.showModal;
        this.cdr.markForCheck();
        this.refreshValues();
      });
  }

  refreshValues() {
    this.store
      .select(AppRouterState.selectUrl)
      .pipe(take(1))
      .subscribe((x) => {
        this.document.relativeUrl = x;
      });

    this.store.pipe(UserState.selectCurrentTruthy(), take(1)).subscribe((x) => {
      const fullName = `${x.Data.FirstName} ${
        x.Data.MiddleName !== undefined && x.Data.MiddleName !== null
          ? ' ' + x.Data.MiddleName
          : ''
      } ${x.Data.LastName}`;
      const email = `${x.Data.Email}${
        x.Data.udUPSEmail !== undefined && x.Data.udUPSEmail !== null
          ? ' | ' + x.Data.udUPSEmail
          : ''
      }`;
      this.ticket.update((t) => ({ ...t, fullName, email }));
      this.document.phoneNumber = x.Data.Phone;
      this.document.smartlookURL = this.smartlookService.sessionUrl();
      this.cdr.markForCheck();
    });
  }

  cancel() {
    this.clearForm();
    this.showModal = false;
  }

  clearForm() {
    this.isSaving = false;
    this.document = {} as HALOHelpDesk;
    // As before, a cleared ticket keeps no category default.
    this.ticket.set(emptyTicketFields(''));
  }

  confirm() {
    this.isSaving = true;
    if (!this.isTicketValid()) return;
    this.saveTicket();
  }

  saveTicket() {
    // US 34308: app, version, user, route, job/company and device ride along so
    // HaloITSM agents can triage without calling the user back.
    const fields = this.ticket();
    // The select's value is the ticket type (was ticketTypeChanged).
    Object.assign(this.document, fields, { ticketType: fields.ticketTypeId });
    const clientContext = buildHelpdeskClientContext({
      appName: helpdeskAppName(environment),
      appVersion: this.version,
      userName: this.document.fullName?.replace(/\s+/g, ' '),
      userEmail: this.document.email,
      route: this.document.relativeUrl,
      device: webHelpdeskDevice(
        globalThis.navigator?.userAgent,
        globalThis.navigator?.maxTouchPoints ?? 0
      ),
    });
    const payload = buildHaloTicketPayload(this.document, clientContext);
    this.helpManagementService.createTicket(payload).subscribe(() => {
      this.clearForm();
      this.showModal = false;
      this.cdr.markForCheck();
    });
  }
}

/** The user-edited half of a ticket: what the signal form binds. */
export interface HaloTicketFields {
  fullName: string;
  email: string;
  ticketTypeId: string;
  summary: string;
  detailsHTML: string;
  impact: string;
  urgency: string;
  category: string;
}

function emptyTicketFields(category: string): HaloTicketFields {
  return {
    fullName: '',
    email: '',
    ticketTypeId: '',
    summary: '',
    detailsHTML: '',
    impact: '',
    urgency: '',
    category: category ?? '',
  };
}

export class HALOHelpDesk {
  ticketType?: string;
  summary?: string;
  detailsHTML?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  category_1?: string;
  customFields?: HALOCustomField[];
  userId?: number;
  fullName?: string;
  relativeUrl?: string;
  phoneNumber?: string;
  email?: string;
  smartlookURL?: string;
  impact?: string;
  urgency?: string;
  category: string;
}

export class HALOCustomField {
  id: number;
  value: number;
  description: string;
}

export interface Category {
  guid: string;
  id: number;
  value: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  category_name: string;
  type_id: number;
}
